// React entrypoint. Boots <App /> inside <BrowserRouter> so every
// path is a React route (marketing + authenticated app share one
// router tree per ADR 0030).

// Global stylesheets, imported (not <link>ed from public/) so Vite
// content-hashes them for cache-busting and hot-reloads them in dev.
// Order matters: marina-tokens.generated.css holds every colour of the Light,
// Dark and Night looks, generated from docs/design/marina-system/tokens.json
// (cloud ADR 0396, TOM-676), and tokens.css the type, shape, spacing and motion.
// marketing.css aliases its older names (--navy-900, --teal-500, …) to them, and
// admin.css reads those, so both come after. ui.css is the shared component
// layer (.tb-*).
import './styles/marina-tokens.generated.css';
import './styles/tokens.css';
import './styles/ui.css';
import './styles/marketing.css';
// The public status page (TOM-930).
import './styles/status.css';
// The public site's header, footer and home page, and their self-hosted fonts
// (TOM-980). A face downloads only when a rule uses it, so other pages pay nothing.
import './styles/marketing-fonts.css';
import './styles/site.css';
import './styles/admin.css';
// Dashboard redesign (device-manuals-forward). Loaded last so its authed-app
// component styles win over marketing's; isolated to class-scoped selectors so
// it can't affect the marketing or admin surfaces.
import './styles/dashboard.css';
// Chart library tokens + chrome (components/charts, ADR 0131). Class-scoped
// (.chart-*, .chartlab-*) plus the --chart-* custom properties.
import './styles/charts.css';
import './styles/toolkit.css';
import './styles/displays.css';
import './styles/owners.css';
// A boat's home photo in Settings (TOM-683).
import './styles/boat-photo.css';
// A boat's Protect key and certificate in Settings (TOM-835).
import './styles/protect.css';
import './styles/operator-token.css';
// The anchor, water and mode cards on a board (TOM-374), on the v2 card.
import './styles/board-cards.css';
// Your boards, the boat's list of boards (TOM-192).
import './styles/boards-page.css';
// A board in the new design: head, grid by size, change marks, Undo, editing (TOM-192).
import './styles/board-page.css';
// Alarms page redesign. `alarms.css` declares the --al-* custom properties on
// .al-page and every shared piece; the three screen sheets follow it and use
// them, so it must come first.
import './styles/alarms.css';
import './styles/alarms-active.css';
import './styles/alarms-all.css';
// The inline editor a row expands into — after alarms-all.css, since it styles
// what happens INSIDE that screen's cards and reuses its slot/button tokens.
import './styles/alarms-editor.css';
// The add-alarm picker panel — after the editor sheet, whose slot and number
// field skins its pre-filled sentence reuses verbatim.
import './styles/alarms-picker.css';
import './styles/alarms-history.css';
// The mate bar, its proposal card and its receipt — page chrome above the three
// screens, so it loads after them and reuses their tokens.
import './styles/alarms-mate.css';
// The AI Watch Assistant page (ADR 0196) — class-scoped .watch-*.
import './styles/watch.css';
// The full-page Neo chat and the Chats list (TOM-195).
import './styles/neo-page.css';
// The one chat box every chat uses: + menu, files, mic, send and Stop (TOM-671).
import './styles/chat-composer.css';
// Neo's receipt under an answer: board changes with Open board and Undo (TOM-354).
import './styles/neo-receipt.css';
// After a 👎, what was wrong with the answer (TOM-461).
import './styles/neo-feedback.css';
// Edit with Neo: the panel beside the page on a desktop, a sheet on a phone (TOM-356).
import './styles/neo-panel.css';
import './styles/edit-chat.css';
import './styles/sparkle-chat.css';
// The conversation workspace: a board beside the chat page on a desktop (TOM-357).
import './styles/neo-workspace.css';
// Neo's maps, in an answer and on a board (TOM-468), on the chart's frame.
import './styles/neo-map.css';
// Neo's diagrams, in an answer and on a board (TOM-643), on the same frame.
import './styles/neo-diagram.css';
// Neo's pictures, in an answer and large (TOM-646), on the same frame.
import './styles/neo-picture.css';
// Neo's pages in an answer and their expand panel (TOM-871), on the same frame.
import './styles/neo-chat-page.css';
// The boat's cameras: the Cameras page, its player, and alarm snapshots (TOM-797).
import './styles/cameras.css';
// Neo's answers in the chat (TOM-709): conclusion first, the brief source line,
// the visual's card, Copy and Helpful, the follow-ups, and the page's header.
// After the card styles, so the chat's own card frame wins in the chat only.
import './styles/neo-answer.css';
// Behind the answer beside the chat on the desktop web, and its Sources (TOM-710).
import './styles/neo-behind.css';
import './styles/slide-chat.css';
// The boat shell after the Neomaris v2 app prototype (TOM-193): header with Neo's
// headline, tab bar, sidebar, menu panel and mode selector (TOM-526). Last, so it
// re-bases the height locks.
import './styles/shell.css';
// The boat's home: the mode's board under a fixed header (TOM-510).
import './styles/home.css';
// Neo's status and the alarm banner in home's slots (TOM-512).
import './styles/home-status.css';
// The boat's equipment and the owner's answers about it (TOM-409).
import './styles/equipment.css';
// Try it and the demo banner (TOM-868).
import './styles/demo.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { AuthProvider } from './auth';
import { Analytics } from './components/Analytics';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { reloadOnStaleBuild } from './lib/staleBuild';

// Before the first render, so a lazy route that fails on a stale build's chunk
// is caught from the start (ADR 0243).
reloadOnStaleBuild();

const container = document.getElementById('root');
if (container === null) {
  throw new Error('No #root mount point in index.html');
}

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        {/* PostHog, only when the server has a key (TOM-983). */}
        <Analytics />
        <RouteErrorBoundary>
          <App />
        </RouteErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>
);
